import type { TradeDataItem, TradeDataFacet } from "../../types/tradeData";
import { rca, cog, distance } from "../../types/branded";
import type {
  CpyQuery,
  CcpyQuery,
  GgpyQuery,
  CgpyQuery,
  GcpyQuery,
  CcyQuery,
  Ccpy2Query,
  Cgpy2Query,
  Gcpy2Query,
} from "../__generated__/graphql";

/**
 * Union of all explore-data query result types.
 * Useful for typing Apollo `data` results from dynamically selected queries.
 */
export type ExploreDataQuery =
  | CpyQuery
  | CcpyQuery
  | GgpyQuery
  | CgpyQuery
  | GcpyQuery
  | CcyQuery
  | Ccpy2Query
  | Cgpy2Query
  | Gcpy2Query;

/**
 * Base record type for fields shared across all explore data query results.
 * Derived from the intersection of CountryProductYear, CountryCountryProductYear,
 * CountryCountryYear, GroupGroupProductYear, CountryGroupProductYear, and
 * GroupCountryProductYear generated types.
 */
export interface ExploreDataRecord {
  __typename?: string;
  year?: number | null;
  exportValue?: number | null;
  importValue?: number | null;
  // Location fields (present in most, absent in CCY which uses countryId/partnerCountryId)
  countryId?: string | null;
  locationLevel?: string | null;
  // Product fields (absent in CCY)
  productId?: string | null;
  productLevel?: number | null;
  // Partner fields (varies by query type)
  partnerCountryId?: string | null;
  partnerLevel?: string | null;
  partnerGroupId?: string | null;
  partnerType?: string | null;
  groupId?: string | null;
  // CPY-specific complexity fields
  exportRca?: number | null;
  isNew?: boolean | null;
  productStatus?: string | null;
  cog?: number | null;
  distance?: number | null;
}

/** Type-safe accessor for the shared `.data` field across all ExploreDataQuery types */
export const getExploreDataArray = (
  response: { data?: (ExploreDataRecord | null)[] | null },
): ExploreDataRecord[] | undefined =>
  (response.data?.filter(
    (d): d is NonNullable<typeof d> => d !== null,
  ) as ExploreDataRecord[] | undefined) ?? undefined;

/**
 * Converts ExploreDataRecords to TradeDataItems, filtering out records
 * missing required fields (year, exportValue, importValue).
 *
 * When the query facet is known, pass it as the second argument to produce
 * items with the `facet` discriminant set, enabling downstream consumers
 * to narrow on the facet for type-safe field access.
 */
export function toTradeDataItems(
  records: ExploreDataRecord[],
  facet?: TradeDataFacet,
): TradeDataItem[] {
  return records
    .filter(
      (r): r is ExploreDataRecord & { year: number; exportValue: number; importValue: number } =>
        r.year != null && r.exportValue != null && r.importValue != null
    )
    .map((r) => ({
      ...(facet !== undefined ? { facet } : {}),
      year: r.year,
      exportValue: r.exportValue,
      importValue: r.importValue,
      productId: r.productId ?? undefined,
      countryId: r.countryId ?? undefined,
      partnerCountryId: r.partnerCountryId ?? undefined,
      partnerGroupId: r.partnerGroupId ?? undefined,
      groupId: r.groupId ?? undefined,
      productLevel: r.productLevel ?? undefined,
      locationLevel: r.locationLevel as string | undefined,
      partnerLevel: r.partnerLevel as string | undefined,
      exportRca: r.exportRca != null ? rca(r.exportRca) : undefined,
      isNew: r.isNew ?? undefined,
      cog: r.cog != null ? cog(r.cog) : undefined,
      distance: r.distance != null ? distance(r.distance) : undefined,
    }));
}
